import { useForm } from 'react-hook-form';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ChoiceGroup } from '@/components/ui/choice-group';
import { FORM_ERROR, errorMessage } from '@/features/auth/use-auth-error';

import { CHOICE_ERROR, asList, type StepProps } from './step-props';
import { useSaveStep } from './use-save-step';

interface AvailabilityForm {
  desiredHours: string;
  desiredShifts: string[] | false;
  desiredWorkday: string[] | false;
}

export function AvailabilityStep({ record, lookups, onNext }: StepProps) {
  const save = useSaveStep('availability', onNext);
  const isLocal = record.primaryWorkType === 'local';
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitted },
  } = useForm<AvailabilityForm>({
    defaultValues: {
      desiredHours: record.desiredHours ?? '',
      desiredShifts: record.desiredShifts ?? [],
      desiredWorkday: record.desiredWorkday ?? [],
    },
  });

  const submit = (v: AvailabilityForm) => {
    save.mutate({
      desiredHours: v.desiredHours,
      desiredShifts: asList(v.desiredShifts),
      desiredWorkday: isLocal ? asList(v.desiredWorkday) : [],
    });
  };

  const hasFieldErrors = isSubmitted && Object.keys(errors).length > 0;

  return (
    <div className="stack stack--loose">
      <div>
        <h1>Tell us about when you&apos;d like to work...</h1>
        <hr className="divider" />
      </div>
      {save.isError && <Alert>{errorMessage(save.error)}</Alert>}
      {!save.isError && hasFieldErrors && <Alert>{FORM_ERROR}</Alert>}

      <form
        className="stack stack--loose"
        noValidate
        onSubmit={(e) => void handleSubmit(submit)(e)}
      >
        <ChoiceGroup
          type="radio"
          name="desiredHours"
          legend="How many hours would you like to work per week?"
          size="lg"
          options={lookups.desiredHours ?? []}
          error={errors.desiredHours?.message}
          registration={register('desiredHours', { required: CHOICE_ERROR })}
        />
        <ChoiceGroup
          type="checkbox"
          name="desiredShifts"
          legend="What hours do you prefer to work? (Select all that apply)"
          size="lg"
          options={lookups.desiredShifts ?? []}
          registration={register('desiredShifts')}
        />
        {isLocal && (
          <ChoiceGroup
            type="checkbox"
            name="desiredWorkday"
            legend="What days do you prefer to work? (Select all that apply)"
            size="lg"
            inline
            options={lookups.desiredWorkday ?? []}
            registration={register('desiredWorkday')}
          />
        )}
        <Button type="submit" busy={save.isPending}>
          Save &amp; continue
        </Button>
      </form>
    </div>
  );
}
